/* KANON `Cwiczenia` 17.09.2026, the CREATOR — variant **g · Trzy kroki**, in one stylesheet.
 *
 * `docs/design/cwiczenia-propozycje-2026-09-17/3-kreator.html#g` is the interactive specimen and
 * `kanon-03-kreator-krok{1,2,3}-{light,dark}.png` the frozen read-back. This file is the specimen's
 * own `kp-` block transcribed onto tokens: the dialog, the rail, the three step bodies and the
 * footer are ONE surface the trainer walks through, and splitting them into five sheets would put
 * five near-identical `.block` rules two pixels apart — the failure the token rule exists to
 * prevent.
 *
 * ── Three rules this round adds, and they hold in every block below ─────────────────────────
 * · **No ember anywhere but the one primary CTA.** The active rail item is a 2px INK bar, a chosen
 *   type tile is an ink border over `--bg-inset`, a chosen metric row the same. The accent appears
 *   exactly once on this dialog.
 * · **Nothing readable sits in `--text-3`.** Measured at 3.01:1 in Flame and 4.02:1 in Flame Dark,
 *   so every description, every constraint line, the rail's echo and the required marker read
 *   `--text-2`. `--text-3` is left to the drop zone's oversized ornament glyph, which carries no
 *   words, and to `.fromProposal`, which board D's canon draws in it (see that block).
 * · **No cards inside a step.** Grouping is whitespace and at most one hairline rule.
 *
 * ── The size is FIXED, and the cap is the only thing that moves ─────────────────────────────
 * Owner decision D11: one size on every step, so „Dalej" never moves. `--h-ex-wizard` is 820px flat
 * and the `min(…, 100dvh − …)` cap is what survives a 620px window — the CONTENT scrolls inside
 * `.content`, which is the one scroller, and the rail and the footer stay put.
 *
 * Heights and widths are layout rather than design tokens (see `stylelint.config.js`), but every
 * one of the wizard's own is named in `tokens/spacing.css` anyway, because the canon measured them.
 */

/* ── The scrim and the box ──────────────────────────────────────────────────────────────────── */

.backdrop {
  position: fixed;
  inset: var(--space-0);
  z-index: 30;
  background: var(--scrim-theatre);
  backdrop-filter: var(--blur-scrim);
  transition-duration: var(--dur-fast);
  transition-property: opacity;
  transition-timing-function: var(--ease);
}

.backdrop[data-starting-style],
.backdrop[data-ending-style] {
  opacity: 0;
}

/* Centred by translation rather than by flexbox on the backdrop: the two are siblings under the
 * portal, not parent and child, so there is no box to centre within. `dvh` and not `vh` — on a
 * laptop whose browser chrome collapses on scroll, `vh` resolves against the TALLER viewport and
 * the footer ends up in the overhang, with „Zapisz ćwiczenie" in it. */
.popup {
  position: fixed;
  top: 50%;
  left: 50%;
  z-index: 31;
  display: flex;
  width: min(var(--w-ex-wizard), calc(100vw - var(--space-10)));
  height: min(var(--h-ex-wizard), calc(100dvh - var(--space-10)));
  overflow: hidden;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-md);
  outline: none;
  box-shadow: var(--shadow-modal);

  /* `body` sets `--lh-relaxed` app-wide, and the sixty-odd micro-labels in this dialog inheriting
   * prose leading is what pushed the theatre's rail 3.5px down. Carried across for the same reason. */
  line-height: var(--lh-auto);
  transform: translate(-50%, -50%);
  transition-duration: var(--dur-fast);
  transition-property: opacity, transform;
  transition-timing-function: var(--ease);
}

.popup[data-starting-style],
.popup[data-ending-style] {
  opacity: 0;
  transform: translate(-50%, calc(-50% + var(--space-1)));
}

/* ── The rail that teaches ──────────────────────────────────────────────────────────────────── */

.rail {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-1);
  width: var(--w-ex-wizard-rail);
  padding: var(--space-22px) var(--space-14px);
  overflow-y: auto;
  background: var(--bg-inset);
  border-right: var(--border-hairline) solid var(--border);
}

.railTitle {
  margin: var(--space-0) var(--space-0) var(--space-14px) var(--space-10px);
  font-size: var(--type-control);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-hint);
  color: var(--text-2);
}

.rails {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.step {
  position: relative;
  display: grid;
  grid-template-columns: var(--sz-ex-wizard-dot) 1fr;
  gap: var(--space-11px);
  align-items: start;
  width: 100%;
  padding: var(--space-10px) var(--space-10px) var(--space-3);
  font: inherit;
  text-align: left;
  cursor: pointer;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
}

.step:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.stepDot {
  display: grid;
  place-items: center;
  width: var(--sz-ex-wizard-dot);
  height: var(--sz-ex-wizard-dot);
  margin-top: var(--space-1px);
  font-size: var(--fs-badge);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-none);
  color: var(--text-2);
  font-variant-numeric: tabular-nums;
  border: var(--border-thick) solid var(--border-2);
  border-radius: var(--r-circle);
}

.stepDotGlyph {
  width: var(--space-11px);
  height: var(--space-11px);
}

.stepTitle {
  display: block;
  font-size: var(--fs-label-md);
  font-weight: var(--fw-bold);
  line-height: var(--lh-hint-dense);
  color: var(--text-2);
}

.stepDesc,
.stepEcho {
  display: block;
  margin-top: var(--space-3px);
  font-size: var(--fs-micro);
  line-height: var(--lh-hint);
  color: var(--text-2);
}

.stepDesc {
  font-weight: var(--fw-medium);
}

.stepEcho {
  font-weight: var(--fw-semibold);
}

.stepDone .stepDot {
  color: var(--bg);
  background: var(--text);
  border-color: var(--text);
}

.stepDone .stepTitle,
.stepCurrent .stepTitle {
  color: var(--text);
}

.stepCurrent {
  background: var(--bg-card);
}

/* The active marker: a 2px INK bar, never the accent rule the earlier variant painted. A rail of
 * rows is not a place for the scalpel. */
.stepCurrent::before {
  position: absolute;
  top: var(--space-10px);
  bottom: var(--space-3);
  left: var(--space-0);
  width: var(--border-focus);
  content: '';
  background: var(--text);
  border-radius: var(--radius-1px);
}

.stepCurrent .stepDot {
  color: var(--text);
  border-color: var(--text);
  box-shadow: var(--ring-wizard-dot);
}

/* ── The body, and the one scroller ─────────────────────────────────────────────────────────── */

.body {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

.content {
  flex: 1;
  min-height: 0;
  padding: var(--space-26px) var(--space-7) var(--space-6);
  overflow-y: auto;
}

.heading {
  margin: var(--space-0) var(--space-0) var(--space-20px);
  font-size: var(--fs-ex-wizard-h);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-name);
  color: var(--text);
  letter-spacing: var(--ls-title);
}

/* Board D's `.from`: the line under a step's heading saying Buddy's proposal filled it, in the
 * board's own tertiary ink. The one readable line in the creator at `--text-3`: board D is the
 * owner-approved canon for it, and a KANON colour is not swapped here. */
.fromProposal {
  margin: var(--space-0) var(--space-0) var(--space-20px);
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-relaxed);
  color: var(--text-3);
}

.heading:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.wide {
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
  width: var(--w-ex-wizard-col);
  max-width: 100%;
}

.wideFull {
  width: 100%;
}

.cols {
  display: grid;
  gap: var(--space-30px);
  align-items: start;
}

.colsBasics {
  grid-template-columns: 1.06fr 1fr;
}

.colsBody {
  grid-template-columns: 1.12fr 1fr;
}

.colsEven {
  grid-template-columns: 1fr 1fr;
}

.col {
  display: flex;
  flex-direction: column;
  gap: var(--space-26px);
  min-width: 0;
}

.block {
  display: flex;
  flex-direction: column;
  gap: var(--space-10px);
  min-width: 0;
}

.blockTight {
  gap: var(--space-7px);
}

.label {
  display: flex;
  gap: var(--space-2);
  align-items: baseline;
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  line-height: var(--lh-hint-dense);
  color: var(--text);
}

/* „wymagane" as a WORD at `--text-2`, never an asterisk and never both conventions. */
.required {
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-hint-dense);
  color: var(--text-2);
}

.hint {
  margin: var(--space-0);
  font-size: var(--fs-label);
  font-weight: var(--fw-medium);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
}

.input {
  width: 100%;
  height: var(--h-ex-wizard-field);
  padding: var(--space-0) var(--space-13px);
  font-family: var(--font-body);
  font-size: var(--fs-dialog-field);
  font-weight: var(--fw-medium);
  line-height: var(--h-ex-wizard-field);
  color: var(--text);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-sm);
  outline: none;
}

.input::placeholder {
  color: var(--text-2);
}

.input:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.textarea {
  height: auto;
  padding: var(--space-11px) var(--space-13px);
  line-height: var(--lh-relaxed);
  resize: none;
}

.rowInput {
  height: var(--h-ex-wizard-row);
  font-size: var(--fs-label-md);
  line-height: var(--h-ex-wizard-row);
}

.counter {
  align-self: flex-end;
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-hint-dense);
  color: var(--text-2);
  font-variant-numeric: tabular-nums;
}

/* ── The type tiles — NEUTRAL surface, hue on the glyph (owner decision D9) ─────────────────── */

.types {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-10px);
}

/* Compact (owner decision D20, 18 September 2026): no height beyond what the content needs, and
 * the three tiles in a row share its height through the grid's own stretch. The glyph stands ABOVE
 * the title (polish of 4 November 2026): beside it, „Wytrzymałość” and „Plyometryczne” did not fit
 * a tile at 1512 and broke mid-word. */
.type {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--space-6px);
  align-content: start;
  align-items: center;
  padding: var(--space-11px) var(--space-3);
  font: inherit;
  text-align: left;
  cursor: pointer;
  background: transparent;
  border: var(--border-thick) solid var(--border-2);
  border-radius: var(--radius-12px);
}

.type:hover {
  background: var(--hover-tint);
}

.type:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.type[aria-checked='true'] {
  background: var(--bg-inset);
  border-color: var(--text);
}

/* The ONE coloured thing on a tile. Six washed cards were rejected (D9): they read as decoration
 * and flatten the hierarchy, where one coloured drawing per tile encodes the same thing quietly. */
.typeGlyph {
  display: block;
  width: var(--sz-ex-wizard-glyph);
  height: var(--sz-ex-wizard-glyph);
  color: var(--type-ink);
}

.typeName {
  font-size: var(--fs-body);
  font-weight: var(--fw-bold);
  line-height: var(--lh-name);
  color: var(--text);
  min-width: 0;
}

.typeDesc {
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  line-height: var(--lh-hint);
  color: var(--text-2);
}

/* ── The metric rows ───────────────────────────────────────────────────────────────────────── */

.mets {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--space-1) var(--space-14px);
}

.met {
  display: grid;
  grid-template-columns: var(--sz-ex-wizard-box) 1fr auto;
  gap: var(--space-11px);
  align-items: start;
  padding: var(--space-9px) var(--space-10px);
  font: inherit;
  text-align: left;
  cursor: pointer;
  background: transparent;
  border-color: transparent;
  border-style: solid;
  border-width: var(--border-thick);
  border-radius: var(--radius-mark);
}

.met:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* Drawn EMPTY when off rather than absent, so the grid announces itself as multi-select. */
.metBox {
  display: grid;
  place-items: center;
  width: var(--sz-ex-wizard-box);
  height: var(--sz-ex-wizard-box);
  margin-top: var(--space-1px);
  color: transparent;
  border: var(--border-thick) solid var(--border-2);
  border-radius: var(--radius-5px);
}

.metBoxGlyph {
  width: var(--space-3);
  height: var(--space-3);
}

.metTitle {
  display: block;
  font-size: var(--fs-dialog-field);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-hint-dense);
  color: var(--text);
}

.metDesc {
  display: block;
  margin-top: var(--space-2px);
  font-size: var(--fs-label);
  font-weight: var(--fw-regular);
  line-height: var(--lh-hint);
  color: var(--text-2);
}

.metUnit {
  margin-top: var(--space-1px);
  padding: var(--space-1px) var(--space-6px);
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
  font-variant-numeric: tabular-nums;
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-xs);
}

/* Ink border plus `--bg-inset`, never a full ink fill: a full fill on a two-line row reads as a
 * banner rather than as a selection. */
.met[aria-pressed='true'] {
  background: var(--bg-inset);
  border-color: var(--text);
}

.met[aria-pressed='true'] .metBox {
  color: var(--bg);
  background: var(--text);
  border-color: var(--text);
}

.auto {
  margin-left: var(--space-7px);
  font-size: var(--fs-badge);
  font-weight: var(--fw-bold);
  line-height: var(--lh-hint);
  color: var(--text-2);
  letter-spacing: var(--ls-nav);
  white-space: nowrap;
}

.autoNote {
  display: flex;
  gap: var(--space-3);
  align-items: baseline;
  font-size: var(--fs-label);
  font-weight: var(--fw-medium);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
}

.rule {
  height: var(--border-hairline);
  background: var(--border);
}

.preview {
  display: flex;
  gap: var(--space-9px);
  align-items: baseline;
  font-size: var(--fs-note);
  font-weight: var(--fw-medium);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
  flex-wrap: wrap;
}

.previewValue {
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-relaxed);
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

.info {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  align-items: flex-start;
}

.infoButton {
  display: inline-flex;
  gap: var(--space-7px);
  align-items: center;
  padding: var(--space-0);
  font: inherit;
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-hint);
  color: var(--text-2);
  cursor: pointer;
  background: none;
  border: none;
}

.infoButton:hover {
  color: var(--text);
}

.infoButton:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
  border-radius: var(--radius-4px);
}

.infoMark {
  display: grid;
  place-items: center;
  width: var(--space-4);
  height: var(--space-4);
  font-size: var(--fs-badge);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-none);
  color: var(--text-2);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--r-circle);
}

.infoChevron {
  display: block;
  width: var(--space-3);
  height: var(--space-3);
  transition-duration: var(--dur-fast);
  transition-property: transform;
  transition-timing-function: var(--ease);
}

.infoButton[aria-expanded='true'] .infoChevron {
  transform: rotate(180deg);
}

.frozen {
  max-width: 62ch;
  margin: var(--space-0);
  font-size: var(--fs-note);
  font-weight: var(--fw-medium);
  line-height: var(--lh-lede);
  color: var(--text-2);
}

/* ── The muscle chips, and the figures beside them ──────────────────────────────────────────── */

.mus {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: var(--space-20px);
  align-items: start;
}

.figures {
  display: inline-flex;
  gap: var(--space-10px);
  height: var(--h-ex-wizard-figure);
}

.regions {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.region {
  display: flex;
  flex-direction: column;
  gap: var(--space-7px);
}

.regionTitle {
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-hint);
  color: var(--text-2);
}

.chips {
  display: flex;
  gap: var(--space-6px);
  flex-wrap: wrap;
}

.chip {
  display: inline-flex;
  align-items: stretch;
  height: var(--h-ex-wizard-chip);
  overflow: hidden;
  border: var(--border-thick) solid var(--border-2);
  border-radius: var(--radius-mark);
}

/* The chip clips its own inner buttons, so the ring is drawn on the CHIP. */
.chip:has(:focus-visible) {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.chipBody {
  display: inline-flex;
  gap: var(--space-7px);
  align-items: center;
  height: 100%;
  padding: var(--space-0) var(--space-10px);
  font: inherit;
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-display);
  color: var(--text);
  white-space: nowrap;
  cursor: pointer;
  background: transparent;
  border: none;
}

.chipGlyph {
  display: inline-grid;
  place-items: center;
  width: var(--space-11px);
  font-size: var(--fs-label-md);
  font-weight: var(--fw-bold);
  line-height: var(--lh-none);
}

.chipGlyphMark {
  width: var(--space-11px);
  height: var(--space-11px);
}

.chipDegree {
  padding: var(--space-0) var(--space-9px);
  font: inherit;
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-none);
  color: inherit;
  cursor: pointer;
  background: transparent;
  border: none;
  border-left: var(--border-hairline) solid var(--border-2);
  font-variant-numeric: tabular-nums;
}

.chipPrimary {
  background: var(--text);
  border-color: var(--text);
}

.chipPrimary .chipBody {
  font-weight: var(--fw-bold);
  color: var(--bg);
}

.chipPrimary .chipDegree {
  color: var(--bg);
  border-left-color: var(--bg);
}

.chipSecondary {
  border-color: var(--text);
}

.chipSecondary .chipBody {
  font-weight: var(--fw-bold);
}

.chipSecondary .chipDegree {
  border-left-color: var(--text);
}

.legend {
  margin: var(--space-0);
  font-size: var(--fs-label);
  font-weight: var(--fw-medium);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
}

/* ── The equipment tiles, in their own scroller ─────────────────────────────────────────────── */

/* The box takes the step's height down to the footer (polish of 4 November 2026: it stopped at
 * 430px with 230px of empty dialog under it). The step's content becomes a column on this step
 * alone, the two columns stretch to fill it, and the box grows into what is left — never below its
 * 430px floor, so on a short window the step scrolls rather than the tiles shrinking to a sliver.
 * The height is the dialog's, never the data's: the answer landing still changes ink only. */
.content:has(> .colsBody) {
  display: flex;
  flex-direction: column;
}

.content > .colsBody {
  flex: 1 0 auto;
  align-items: stretch;
}

.colsBody > .col:last-child > .block {
  flex: 1;
}

.equipBox {
  flex: 1 1 0;
  min-height: var(--h-ex-wizard-equip);
  padding-right: var(--space-1);
  overflow-y: auto;
  scrollbar-gutter: stable;
}

.equipGrid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--space-11px);
}

.equipTile {
  display: flex;
  flex-direction: column;
  gap: var(--space-6px);
  align-items: center;
  padding: var(--space-0);
  font: inherit;
  cursor: pointer;
  background: none;
  border: none;
}

.equipTile:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
  border-radius: var(--radius-sm);
}

/* `--bg-card` and not the specimen's literal `#fff`: a theme-blind white square is a bright hole on
 * the dark canvas, and the plate is only ever seen while the picture is in flight or after it
 * fails. The same choice the list's own equipment tiles record (deviations register, entry 8). */
.equipPlate {
  position: relative;
  width: 100%;
  overflow: hidden;
  aspect-ratio: 1;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-sm);
}

.equipPhoto {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.equipLetter {
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  font-size: var(--fs-h3);
  font-style: normal;
  font-weight: var(--fw-bold);
  line-height: var(--lh-none);
  color: var(--text-2);
}

/* The box sits on a photograph rather than on a theme surface, so it reads the two
 * theme-independent tokens the app already mints for exactly that — a solid plate on a picture and
 * the ink that goes on it. */
.equipCheck {
  position: absolute;
  top: var(--space-6px);
  right: var(--space-6px);
  display: grid;
  place-items: center;
  width: var(--sz-ex-wizard-check);
  height: var(--sz-ex-wizard-check);
  color: transparent;
  background: var(--plate-on-photo);
  border: var(--border-thick) solid var(--edge-on-plate);
  border-radius: var(--radius-5px);
}

.equipCheckGlyph {
  width: var(--space-10px);
  height: var(--space-10px);
}

.equipLabel {
  overflow: hidden;
  width: 100%;
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-hint-dense);
  color: var(--text-2);
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.equipTile[aria-pressed='true'] .equipPlate {
  border: var(--border-thick) solid var(--text);
}

.equipTile[aria-pressed='true'] .equipCheck {
  color: var(--plate-on-photo);
  background: var(--text-on-plate);
  border-color: var(--text-on-plate);
}

.equipTile[aria-pressed='true'] .equipLabel {
  font-weight: var(--fw-bold);
  color: var(--text);
}

/* The wait's tile: geometry only — the fill is `shared/ui/skeleton`'s, and nothing here restates it. */
.equipSkelPlate {
  display: block;
  width: 100%;
  aspect-ratio: 1;
}

.equipSkelLabel {
  display: block;
  width: 60%;
  height: var(--space-3);
}

/* The failure and the empty taxonomy stand in the middle of the same reserved box. */
.equipState {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  align-items: center;
  justify-content: center;
  height: 100%;
}

.equipEmpty {
  margin: var(--space-0);
  font-size: var(--fs-label);
  font-weight: var(--fw-medium);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
}

/* ── The two drop zones, side by side (owner decision D12) ──────────────────────────────────── */

/* `minmax(0, …)`: a `1fr` track's floor is its content's min-content, so a film name with no
 * spaces in it widened its column and squeezed the cover's. The two zones stand at ONE height
 * (polish of 4 November 2026): the row stretches, and each zone fills its cell under its caption. */
.media {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: var(--space-14px);
  align-items: stretch;
}

.dropCell {
  flex: 1;
}

.drop {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--space-7px);
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: var(--space-6) var(--space-4);
  font: inherit;
  text-align: center;
  cursor: pointer;
  background: transparent;
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-12px);
}

.drop:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.dropOver {
  background: var(--hover-tint);
  border-color: var(--text);
}

/* The one place `--text-3` is right on this dialog: it is ornament, and it carries no words. */
.dropGlyph {
  display: block;
  width: var(--sz-ex-wizard-drop);
  height: var(--sz-ex-wizard-drop);
  margin-bottom: var(--space-2px);
  color: var(--text-3);
}

.dropTitle {
  font-size: var(--fs-label-md);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-hint);
  color: var(--text);
}

.dropVerb {
  text-underline-offset: var(--space-2px);
}

/* The lines and a refusal share ONE cell: a refusal stands over the lines, which stay in the cell
 * hidden and hold its height, so the zone does not grow when the refusal lands (see `DropZone`). */
.dropSlot {
  display: grid;
  justify-items: center;
}

.dropSlot > * {
  grid-area: 1 / 1;
}

/* The zone's own 7px rhythm, carried into the cell the lines now stand in. */
.dropLines {
  display: flex;
  flex-direction: column;
  gap: var(--space-7px);
  align-items: center;
}

.dropLinesHeld {
  visibility: hidden;
}

.dropLine {
  overflow-wrap: anywhere;
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  font-variant-numeric: tabular-nums;
  line-height: var(--lh-hint);
  color: var(--text-2);
}

.dropPreview {
  display: block;
  width: 100%;
  object-fit: cover;
  aspect-ratio: 16 / 9;
  border-radius: var(--radius-sm);
}

/* ── The step and link editors ──────────────────────────────────────────────────────────────── */

.rows {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.stepRow {
  display: grid;
  grid-template-columns: var(--h-ex-step-mark) minmax(0, 1fr) var(--sz-ex-wizard-iconbtn);
  gap: var(--space-9px);
  align-items: center;
}

.stepNumber {
  display: grid;
  place-items: center;
  width: var(--h-ex-step-mark);
  height: var(--h-ex-step-mark);
  font-size: var(--fs-caption);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-none);
  color: var(--text-2);
  font-variant-numeric: tabular-nums;
  background: var(--bg-chip);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-xs);
}

.linkRow {
  display: grid;
  grid-template-columns:
    var(--sz-ex-wizard-brand) minmax(0, 1fr) minmax(0, 1fr)
    var(--sz-ex-wizard-iconbtn);
  gap: var(--space-9px);
  align-items: center;
}

/* A brand mark is drawn in its own colours, so it needs a light plate in BOTH themes — the same
 * theme-independent token the play button and the „Twoje" badge already read. */
.brandPlate {
  display: grid;
  place-items: center;
  width: var(--sz-ex-wizard-brand);
  height: var(--sz-ex-wizard-brand);
  color: var(--text-2);
  background: var(--plate-on-photo);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-8px);
}

.brandGlyph {
  width: var(--space-4);
  height: var(--space-4);
}

.iconButton {
  display: grid;
  place-items: center;
  width: var(--sz-ex-wizard-iconbtn);
  height: var(--sz-ex-wizard-iconbtn);
  color: var(--text-2);
  cursor: pointer;
  background: transparent;
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-mark);
}

.iconButton:hover {
  color: var(--text);
  background: var(--hover-tint);
}

.iconButton:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.iconButtonGlyph {
  width: var(--space-15px);
  height: var(--space-15px);
}

.tools {
  display: flex;
  gap: var(--space-3);
  align-items: center;
}

/* „Dodaj krok" and „Dodaj link" — real secondary buttons with a plus (polish of 4 November 2026),
 * the shared ghost's treatment at a step row's height. GEOMETRY only here. */
.addButton {
  gap: var(--space-6px);
  height: var(--h-ex-wizard-row);
  padding: var(--space-0) var(--space-3);
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  background: transparent;
  border-radius: var(--radius-sm);
}

.addButton:hover {
  color: var(--text);
}

.addGlyph {
  display: block;
  flex: none;
  width: 14px;
  height: 14px;
}

/* The refusal a row earned, on the row that earned it. */
.rowRefusal {
  margin: var(--space-0);
  overflow-wrap: anywhere;
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-hint);
  color: var(--danger-text-strong);
}

/* ── The footer ─────────────────────────────────────────────────────────────────────────────── */

.footWrap {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-9px);
  padding: var(--space-13px) var(--space-6) var(--space-15px);
  border-top: var(--border-hairline) solid var(--border);
}

/* The canon's `kp-gate`: empty and zero-high on a complete draft, one line while something is
 * missing. The primary is pinned to the bottom edge either way. */
.gate {
  align-self: flex-end;
  margin: var(--space-0);
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  font-variant-numeric: tabular-nums;
  line-height: var(--lh-relaxed);
  color: var(--text-2);
  text-align: right;
}

.gateDanger {
  color: var(--danger-text-strong);
}

.foot {
  display: flex;
  gap: var(--space-14px);
  align-items: center;
}

.spacer {
  flex: 1;
}

/* Every control here but the primary is a TEXT button. One solid control per screen. */
.textButton {
  padding: var(--space-0);
  font-family: var(--font-body);
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  cursor: pointer;
  background: none;
  border: none;
}

.textButton:hover {
  color: var(--text);
}

.textButton:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
  border-radius: var(--radius-4px);
}

/* The GEOMETRY of the one ember on this dialog. The treatment — fill, ink, border, focus ring — is
 * `shared/ui/emberButton.module.css`'s and nothing here may restate it. */
.primary {
  height: var(--h-ex-wizard-cta);
  padding: var(--space-0) var(--space-5);
  font-size: var(--fs-button-sm);
  font-weight: var(--fw-bold);
  letter-spacing: normal;
  border-radius: var(--radius-12px);
}

/* ── The metrics lock on an EDIT ────────────────────────────────────────────────────────────── */

/* A frozen row is not a control. It keeps the drawing and loses the press, so the trainer can read
 * what the movement measures without being offered a change the contract refuses — and since the
 * polish of 4 November 2026 it LOOKS read-only: no hover, no selected frame, the ticked boxes in a
 * quieter ink and the unticked ones barely drawn. */
.metLocked,
.metLocked:hover {
  cursor: default;
  background: transparent;
}

.metLocked[aria-pressed='true'] {
  background: transparent;
  border-color: transparent;
}

.metLocked[aria-pressed='false'] .metBox {
  border-color: var(--border);
}

.metLocked[aria-pressed='true'] .metBox {
  background: var(--text-2);
  border-color: var(--text-2);
}

.metLocked[aria-pressed='false'] .metTitle {
  color: var(--text-2);
}

.lockNote {
  margin: var(--space-0);
  font-size: var(--fs-label);
  font-weight: var(--fw-medium);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
}
